import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Eraser } from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatCurrency } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import type { CairoPackage, LatePricing } from '@/types/api'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input, Select } from '@/components/ui/Input'
import { Spinner } from '@/components/ui/Spinner'
import { ErrorState } from '@/components/ui/States'
import { previewAreaSurcharge, type TripCheckTiming } from './api'
import { describeEnd, describeEndCharge } from './describe'
import type { TripPins } from './AreaMap'
import styles from './AreaPricingPage.module.css'

interface TripCheckPanelProps {
  trip: TripPins
  /** Whether Cairo is priced by sub-area. */
  enabled: boolean
  latePricing: LatePricing
  /** Cairo's packages, shortest first: the package decides when the trip ends. */
  packages: CairoPackage[]
  onClear: () => void
  onDone: () => void
}

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/

/**
 * When a trip that starts at `pickupTime` on this package ends, as a clock
 * time — "02:30", "23:30 (7 days later)". Display only: whether that end is
 * late is the server's answer.
 */
function endTimeOf(pickupTime: string, durationHours: number | null): string | null {
  if (durationHours === null || !TIME.test(pickupTime)) return null
  const [hours, minutes] = pickupTime.split(':').map(Number)
  const total = hours * 60 + minutes + Math.round(durationHours * 60)
  const days = Math.floor(total / (24 * 60))
  const minuteOfDay = total % (24 * 60)
  const clock = `${String(Math.floor(minuteOfDay / 60)).padStart(2, '0')}:${String(minuteOfDay % 60).padStart(2, '0')}`
  return days >= 2 ? `${clock} (${String(days)} days later)` : clock
}

/**
 * Drop a pickup and a drop-off and see what the trip would add — asked of the
 * server, so it answers with the same sub-areas, the same overlaps and the same
 * prices a booking would be charged with. Works while Cairo is priced as one
 * area, which is when it is most needed: checking the drawing before customers
 * see it.
 *
 * With a package and a pickup time it also prices each end for its time, as
 * though late-night pricing were on: the pickup at the start, the drop-off when
 * the package ends.
 */
export function TripCheckPanel({ trip, enabled, latePricing, packages, onClear, onDone }: TripCheckPanelProps) {
  const { pickup, dropoff } = trip
  const both = pickup && dropoff ? { pickup, dropoff } : null
  const [packageId, setPackageId] = useState<number | null>(packages[0]?.id ?? null)
  const [pickupTime, setPickupTime] = useState('')

  const chosenPackage = packages.find((candidate) => candidate.id === packageId) ?? null
  const timing: TripCheckTiming | null =
    chosenPackage && TIME.test(pickupTime) ? { packageId: chosenPackage.id, pickupTime } : null
  const endsAt = timing ? endTimeOf(timing.pickupTime, chosenPackage?.durationHours ?? null) : null

  const preview = useQuery({
    queryKey: both
      ? queryKeys.areaPricing.preview(
          both.pickup.lat,
          both.pickup.lng,
          both.dropoff.lat,
          both.dropoff.lng,
          timing?.packageId ?? null,
          timing?.pickupTime ?? null,
        )
      : queryKeys.areaPricing.all,
    queryFn: ({ signal }) => previewAreaSurcharge(both!.pickup, both!.dropoff, timing, signal),
    enabled: both !== null,
  })

  const result = both ? preview.data : undefined

  return (
    <Card>
      <CardHeader
        title="Check a trip"
        description="Click the map for the pickup, then the drop-off. Drag either pin to move it."
      />
      <CardBody>
        <div className={styles.panelBody}>
          <dl className={styles.ends}>
            <div className={styles.end}>
              <dt>
                <span className={cn(styles.pinSwatch, styles.pinSwatchPickup)} aria-hidden="true" />
                Pickup
              </dt>
              <dd>
                {result ? (
                  <>
                    <span>{describeEnd(result.pickup)}</span>
                    <span className={styles.rowMeta}>
                      {timing ? `${timing.pickupTime} · ` : ''}
                      {describeEndCharge(result.pickup)}
                    </span>
                  </>
                ) : (
                  <span className={styles.rowMeta}>{pickup ? 'Placed' : 'Click the map'}</span>
                )}
              </dd>
            </div>
            <div className={styles.end}>
              <dt>
                <span className={cn(styles.pinSwatch, styles.pinSwatchDropoff)} aria-hidden="true" />
                Drop-off
              </dt>
              <dd>
                {result ? (
                  <>
                    <span>{describeEnd(result.dropoff)}</span>
                    <span className={styles.rowMeta}>
                      {endsAt ? `${endsAt} · ` : ''}
                      {describeEndCharge(result.dropoff)}
                    </span>
                  </>
                ) : (
                  <span className={styles.rowMeta}>{dropoff ? 'Placed' : pickup ? 'Click the map' : '—'}</span>
                )}
              </dd>
            </div>
          </dl>

          <div className={styles.checkTiming}>
            <Field label="Package">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={packageId ?? ''}
                  onChange={(event) => setPackageId(event.target.value === '' ? null : Number(event.target.value))}
                >
                  {packages.map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Pickup at">
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  type="time"
                  aria-describedby={describedBy}
                  value={pickupTime}
                  onChange={(event) => setPickupTime(event.target.value)}
                />
              )}
            </Field>
          </div>
          {!timing ? (
            <p className={styles.checkHint}>Add a pickup time to see whether either end is late at night.</p>
          ) : null}

          {both && preview.isPending ? (
            <p className={styles.loading}>
              <Spinner size="sm" label={null} /> Working it out…
            </p>
          ) : null}
          {both && preview.isError ? (
            <ErrorState compact error={preview.error} onRetry={() => void preview.refetch()} />
          ) : null}

          {result ? (
            <div className={styles.totals} role="status">
              <div className={styles.total}>
                <span className={styles.totalLabel}>Added to every Cairo package</span>
                <span className={styles.totalValue}>
                  {result.totalMinor > 0 ? `+${formatCurrency(result.total)}` : 'Nothing'}
                </span>
              </div>
              {timing ? (
                <div className={styles.lateTotal}>
                  <span className={styles.totalLabel}>Late-night charge</span>
                  <span className={styles.totalValue}>
                    {result.lateChargeMinor > 0 ? `+${formatCurrency(result.lateCharge)}` : 'Nothing'}
                  </span>
                </div>
              ) : null}
            </div>
          ) : null}

          {result && !enabled ? (
            <p className={styles.hint}>
              Cairo is priced as one area at the moment, so customers are not charged this yet.
            </p>
          ) : result && timing && !latePricing.active && result.lateChargeMinor > 0 ? (
            <p className={styles.hint}>
              Late-night pricing is off at the moment, so customers are not charged the late-night part yet.
            </p>
          ) : null}

          <div className={styles.actions}>
            <Button
              variant="ghost"
              iconLeft={<Eraser aria-hidden="true" />}
              onClick={onClear}
              disabled={!pickup && !dropoff}
            >
              Clear pins
            </Button>
            <Button onClick={onDone}>Done</Button>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}
